import { ChartRequestType, StackedBarChartSettings } from "types/charts/requests";

export const SPLIT_YEAR_ELIGIBLE_UNITS = ["Year-Qtr", "Year-Month"] as const;
export type SplitYearUnit = (typeof SPLIT_YEAR_ELIGIBLE_UNITS)[number];

/** Returns the Split Year unit ("Year-Qtr" / "Year-Month") for a field, or null if not eligible. */
export function getSplitYearUnit(field?: { defaultUnit?: string }): SplitYearUnit | null {
  const unit = field?.defaultUnit;
  return unit === "Year-Qtr" || unit === "Year-Month" ? unit : null;
}

/**
 * The actual splitYearActive value sent to chart-svc. Split Year is meaningless in
 * histogram mode (GroupBy/X already coincide there), so it's suppressed there even if the
 * toggle's stored Redux state is still active from before the user switched into histogram
 * mode. Single source of truth for this suppression rule — used both when building the
 * request (useChartDataController) and when checking whether a past request matches the
 * live toggle state (isSplitYearResponseFresh), so the two can't silently drift apart.
 */
export function getEffectiveSplitYearActive(
  splitYearActive: Maybe<boolean>,
  isHistogramOn: Maybe<boolean>
): boolean {
  return Boolean(splitYearActive) && !isHistogramOn;
}

/**
 * Since the reshape is backend-driven, toggling Split Year means waiting on a real network
 * round trip, not an instant client-side recompute. Compare the actual request that produced
 * the current `apiResponse` against what the request would look like right now — if they
 * don't match, `apiResponse` hasn't caught up to this toggle yet, so callers must not trust
 * its `splitYearApplied` value (or any data shaped by it) until this returns true.
 */
export function isSplitYearResponseFresh(
  request: Maybe<ChartRequestType>,
  splitYearActive: Maybe<boolean>,
  isHistogramOn: Maybe<boolean>
): boolean {
  const requestSplitYearActive = (request?.chartSetting as Maybe<StackedBarChartSettings>)
    ?.splitYearActive;
  return (
    requestSplitYearActive === getEffectiveSplitYearActive(splitYearActive, isHistogramOn)
  );
}

/**
 * True when this field's eligibility disagrees between the two independently-maintained
 * checks: this field's `defaultUnit` (frontend, gates whether the toggle renders at all) vs.
 * chart-svc's `SplitYearDetectedUnit` (backend, derived from the x field's mapping-name
 * suffix — see DateBucketParser.GetEligibleUnit). Nothing keeps these in sync, so a field
 * config error here shows the toggle, sends the flag, and gets a silent no-op from the
 * backend — indistinguishable, from `splitYearApplied` alone, from the field genuinely having
 * no quarter/month buckets in its data. This is a field-config bug, not a data problem, so
 * callers should surface it differently (and report it) rather than blaming the data.
 */
export function isSplitYearFieldMismatch(
  field: Maybe<{ defaultUnit?: string }>,
  detectedUnit: Maybe<string | null>
): boolean {
  return getSplitYearUnit(field) !== null && !detectedUnit;
}
